import { Link } from 'react-router-dom';
import { useT } from '@/shared/i18n';
import { ROUTES } from '@/app/router/route-paths';
import { TypewriterWord } from './TypewriterWord';
import { HeroMockup } from './HeroMockup';
import type { ReactNode } from 'react';

/**
 * Hero — the landing hero (DEC-051). A two-column composition: on the start
 * side, the AI-native badge, a dominant headline whose accent word cycles in
 * Electric Lime via the typewriter, a short subtitle, and two CTAs; on the end
 * side, a code-drawn product preview (`HeroMockup`). Columns stack to a single
 * centered column on mobile and mirror for RTL (the grid follows writing
 * direction). Surface + Lime dotted grid with a soft gradient glow;
 * theme-adaptive and RTL-aware.
 */
export function Hero(): ReactNode {
  const { t } = useT();
  const words = [
    t('landing.hero.word.1'),
    t('landing.hero.word.2'),
    t('landing.hero.word.3'),
  ];
  return (
    <section className="relative overflow-hidden bg-surface-base pb-20 pt-36 md:pb-24 md:pt-44">
      <div aria-hidden="true" className="dotted-grid pointer-events-none absolute inset-0" />

      {/* Soft, centered gradient glow behind the headline — no heavy neon. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-20 -z-0 h-[460px] w-[760px] -translate-x-1/2 rounded-full bg-secondary-fixed/10 blur-[160px]"
      />

      <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-margin-mobile lg:grid-cols-2 lg:gap-10 md:px-margin-desktop">
        {/* Text column */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-start">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[var(--hero-badge-border)] bg-secondary-fixed/5 px-4 py-1.5 backdrop-blur-md">
            <span className="h-2 w-2 animate-pulse rounded-full bg-secondary-fixed" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[var(--ink-accent)]">
              {t('landing.hero.badge')}
            </span>
          </div>

          <h1 className="mb-6 text-balance text-4xl font-bold leading-[1.08] tracking-tight text-on-surface sm:text-5xl">
            {t('landing.hero.lead')} <TypewriterWord words={words} /> {t('landing.hero.tail')}
          </h1>

          <p className="mb-10 max-w-xl text-lg leading-relaxed text-on-surface-variant">
            {t('landing.hero.subtitle')}
          </p>

          <div className="flex w-full flex-col items-stretch gap-4 sm:w-auto sm:flex-row sm:items-center">
            <Link
              to={ROUTES.auth.register}
              data-testid="hero-cta-primary"
              className="rounded-lg bg-secondary-fixed px-10 py-4 text-center text-lg font-semibold text-on-secondary-fixed shadow-xl shadow-secondary-fixed/20 transition-transform hover:scale-105 active:scale-95"
            >
              {t('landing.hero.ctaPrimary')}
            </Link>
            <a
              href="#features"
              className="rounded-lg border border-outline/30 bg-surface-container-low px-10 py-4 text-center text-lg font-semibold text-on-surface transition-colors hover:bg-surface-container-high"
            >
              {t('landing.hero.ctaSecondary')}
            </a>
          </div>
        </div>

        {/* Visual column — code-drawn product preview. */}
        <div className="flex justify-center lg:justify-end">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

export default Hero;
